import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { BackendError } from "../../../config/axios.tsx";
import { APPS } from "../../../constants/AppsConstants.ts";
import { IRootStore } from "../../../mobx/root-store.tsx";
import { BrushMode, IBrushSettings } from "../../../models/brush.ts";
import { PlaygroundImage, PlaygroundResult } from "../../../models/playground.ts";
import { TextToImageConfigType } from "../../../models/text-to-image.ts";
import QueryService from "../../../utils/QueryService.ts";
import useImageUtils from "../../../utils/useImageUtils.tsx";
import useVideoGenerationError from "./Error/useVideoGenerationError.tsx";
import {
	KeyPoint,
	TgVideoGenerationConfigType,
	VIDEO_SEGMENTATION_ASYNC_PROPS,
	VideoSegmentationConfigType,
	VideoSegmentationResponse,
	VideoSegmentationTools,
	defaultKeypointBrushConfigs,
	defaultTgVideoGenerationConfig,
	defaultVideoSegmentationConfig,
} from "./video-generation.ts";

export interface IVideoGenerationStore {
	tgConfig: TgVideoGenerationConfigType;
	mask_url: string;
	merged_video_url: string;
	maskToDisplay: string;
	segmentConfig: VideoSegmentationConfigType;
	errors: { generate?: BackendError; generateMask?: BackendError; mergeMask?: BackendError };
	isGeneratingVideo: boolean;
	isGeneratingMask: boolean;
	abortController: AbortController | null;
	keypointBrushConfigs: IBrushSettings;

	generateTgImageToVideo: (config?: TgVideoGenerationConfigType) => Promise<void>;
	tryAgainTgImageToVideo: (image: PlaygroundImage, resultId: string) => Promise<void>;
	abortVideoGeneration: () => void;
	handleTgConfigChange: <K extends keyof TgVideoGenerationConfigType>(
		key: K,
		value: TgVideoGenerationConfigType[K],
	) => void;
	handleSegmentConfigChange: <K extends keyof VideoSegmentationConfigType>(
		key: K,
		value: VideoSegmentationConfigType[K],
	) => void;
	clearTgConfig: () => void;
	clearSegmentConfig: () => void;
	generateMaskByPrompt: (config?: VideoSegmentationConfigType) => Promise<void>;
	generateMaskByKeyPoints: (config?: VideoSegmentationConfigType) => Promise<void>;
	previewMask: (config?: VideoSegmentationConfigType) => Promise<void>;
	abortVideoSegmentation: () => void;
}

export type KeyPointType = Extract<BrushMode, "point" | "point-erase">;

class VideoGenerationStore implements IVideoGenerationStore {
	private readonly rootStore: IRootStore;
	private readonly queryService: QueryService = new QueryService("/video");
	private readonly imageUtils = useImageUtils();
	private readonly videoGenerationError = useVideoGenerationError();
	private readonly VIDEO_ASYNC_PROPS = {
		statusUrl: "",
		maxAttempts: 180,
		sleepSecs: 2,
	};

	abortController: AbortController | null = null;
	tgConfig: TgVideoGenerationConfigType = defaultTgVideoGenerationConfig;
	segmentConfig: VideoSegmentationConfigType = defaultVideoSegmentationConfig;
	mask_url: string = "";
	merged_video_url: string = "";
	errors: { generate?: BackendError; generateMask?: BackendError; mergeMask?: BackendError } = {};
	isGeneratingVideo = false;
	isGeneratingMask = false;
	keypointBrushConfigs: IBrushSettings = defaultKeypointBrushConfigs;

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	get maskToDisplay(): string {
		if (this.merged_video_url) return this.merged_video_url;
		if (this.errors.mergeMask) return this.mask_url;
		return "";
	}

	async generateTgImageToVideo(config?: TgVideoGenerationConfigType, tryAgainResultId?: string): Promise<void> {
		const _config = config || this.tgConfig;
		this._initializeGeneration(["generate"]);
		this.isGeneratingVideo = true;

		const { playgroundStore } = this.rootStore;
		const savedIndex = tryAgainResultId
			? playgroundStore.playgroundResults.findIndex((result) => result.id === tryAgainResultId)
			: playgroundStore.playgroundResults.length;

		if (tryAgainResultId) {
			playgroundStore.playgroundResults[savedIndex].video = this._createEmptyVideoSkeleton();
		} else {
			const resultSkeleton = this._createVideoSkeleton(_config);
			playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultSkeleton];
		}

		try {
			const statusResult: { status_url: string } = await this.queryService.post(
				"/generate/from-tailored-image",
				_config,
				{ "Content-Type": "application/json" },
				{ signal: this.abortController!.signal },
			);

			const result: { video_url: string } = await this.imageUtils.pullUntilAvailableV2("", {
				statusUrl: statusResult.status_url,
				isV2: true,
				maxAttempts: 180,
				sleepSecs: 2,
				abortController: this.abortController,
			});

			playgroundStore.playgroundResults[savedIndex].video = {
				src: result.video_url,
			};
			this.isGeneratingVideo = false;
		} catch (err: any) {
			this.isGeneratingVideo = false;
			this._handleError(err, "generate", "Video generation failed", () =>
				this._removeResultAtIndex(playgroundStore, savedIndex),
			);
			this._removeResultAtIndex(playgroundStore, savedIndex);
			return Promise.reject(err);
		}
	}

	async tryAgainTgImageToVideo(image: PlaygroundImage, resultId: string): Promise<void> {
		await this.generateTgImageToVideo(
			{
				image: image.url,
				model_id: (image.config as TextToImageConfigType)?.model_id as string,
				seed: (image.config as TextToImageConfigType)?.seed,
			},
			resultId,
		);
	}

	async generateMaskByPrompt(config?: VideoSegmentationConfigType): Promise<void> {
		const _config = config || this.segmentConfig;
		await this._generateAndMergeMask(
			"mask_by_prompt",
			{ video: _config?.video, prompt: _config?.prompt },
			_config.video,
		);
	}

	async generateMaskByKeyPoints(config?: VideoSegmentationConfigType): Promise<void> {
		const _config = config || this.segmentConfig;
		await this._generateAndMergeMask(
			"mask_by_keypoints",
			{ video: _config?.video, key_points: _config?.key_points, frame_index: _config?.frame_index },
			_config.video,
		);
	}

	async previewMask(config?: VideoSegmentationConfigType): Promise<void> {
		const _config = config || this.segmentConfig;
		this._initializeGeneration(["generateMask", "mergeMask"]);
		this.isGeneratingMask = true;

		try {
			if (!_config.mask_url) {
				this.isGeneratingMask = false;
				return;
			}

			this.mask_url = _config.mask_url || "";
			this.merged_video_url = this.mask_url;
			this.isGeneratingMask = false;

			// try {
			// 	await this._handleMaskMerge(_config.mask_url, _config.video);
			// 	this.isGeneratingMask = false;
			// } catch {
			// 	return;
			// }
		} catch (err: any) {
			this.isGeneratingMask = false;
			this._handleError(err, "generateMask", "Video segmentation failed", () => {
				this.mask_url = "";
			});
		}
	}

	async mergeMaskWithVideo(mask_url: string, video_url: string, signal?: AbortSignal): Promise<string> {
		const payload = { mask_url, video_url };
		const mergedVideoUrl = await this.queryService.post("/segment/merge_mask_with_video", payload, undefined, {
			signal,
		});
		return mergedVideoUrl;
	}

	private async _generateAndMergeMask(
		endpoint: VideoSegmentationTools,
		payload:
			{ video?: string; prompt?: string } | { video?: string; key_points?: KeyPoint[]; frame_index?: number },
		_video_url: string,
	): Promise<void> {
		this._initializeGeneration(["generateMask", "mergeMask"]);
		this.isGeneratingMask = true;

		try {
			const statusResult: { status_url: string } = await this.queryService.post(
				`/segment/${endpoint}`,
				payload,
				{ "Content-Type": "application/json" },
				{ signal: this.abortController!.signal },
			);

			const result: VideoSegmentationResponse = await this.imageUtils.pullUntilAvailableV2("", {
				statusUrl: statusResult.status_url,
				isV2: true,
				...VIDEO_SEGMENTATION_ASYNC_PROPS[endpoint],
				abortController: this.abortController,
			});

			if (!result.mask_url) {
				this.isGeneratingMask = false;
				return;
			}

			this.mask_url = result.mask_url;
			this.merged_video_url = this.mask_url;
			this.isGeneratingMask = false;

			// try {
			// 	await this._handleMaskMerge(result.mask_url, video_url);
			// 	this.isGeneratingMask = false;
			// } catch {
			// 	this.merged_video_url = this.mask_url;
			// 	this.isGeneratingMask = false;
			// }
		} catch (err: any) {
			this.isGeneratingMask = false;
			err.message = this.videoGenerationError.mapVideoGenerationError(err.message);
			this._handleError(err, "generateMask", "Video segmentation failed", () => {
				this.mask_url = "";
			});
		}
	}

	private _setError(key: keyof typeof this.errors, err: any, defaultMessage: string): void {
		this.errors[key] = {
			message: err.response?.data?.message || err.message || defaultMessage,
			status: err.response?.status || 500,
		};
	}

	private _initializeGeneration(errorKeys: Array<keyof typeof this.errors>): void {
		this.abortController = new AbortController();
		this.errors = errorKeys.reduce((acc, key) => ({ ...acc, [key]: undefined }), { ...this.errors });
	}

	private _handleError(
		err: any,
		errorKey: keyof typeof this.errors,
		defaultMessage: string,
		onAbort?: () => void,
	): void {
		if (this.abortController?.signal?.aborted) {
			onAbort?.();
			return;
		}
		this._setError(errorKey, err, defaultMessage);
	}

	handleTgConfigChange = <K extends keyof TgVideoGenerationConfigType>(
		key: K,
		value: TgVideoGenerationConfigType[K],
	) => {
		this.tgConfig[key] = value;
	};

	handleSegmentConfigChange = <K extends keyof VideoSegmentationConfigType>(
		key: K,
		value: VideoSegmentationConfigType[K],
	) => {
		this.segmentConfig[key] = value;
	};

	clearTgConfig = () => {
		this.tgConfig = defaultTgVideoGenerationConfig;
	};

	clearSegmentConfig = () => {
		this.segmentConfig = defaultVideoSegmentationConfig;
		this.keypointBrushConfigs = defaultKeypointBrushConfigs;
		this.mask_url = "";
		this.merged_video_url = "";
	};

	abortVideoSegmentation = () => {
		if (this.abortController) {
			this.abortController.abort();
		}
		this.isGeneratingMask = false;
	};

	abortVideoGeneration = () => {
		if (this.abortController) {
			this.abortController.abort();
		}
		this.isGeneratingVideo = false;
	};

	private _createVideoSkeleton(config: TgVideoGenerationConfigType): PlaygroundResult {
		return {
			id: uuidv4(),
			config: { ...config } as TgVideoGenerationConfigType,
			type: APPS.VIDEO_GENERATION,
			images: [],
			video: {
				src: "",
				asyncProps: this.VIDEO_ASYNC_PROPS,
			},
		};
	}

	private _createEmptyVideoSkeleton() {
		return {
			src: "",
			asyncProps: this.VIDEO_ASYNC_PROPS,
		};
	}

	private _removeResultAtIndex(playgroundStore: any, index: number): void {
		playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
			(_: any, i: number) => i !== index,
		);
	}

	// private async _handleMaskMerge(mask_url: string, video_url: string): Promise<void> {
	// 	const mergedVideoUrl = await this.mergeMaskWithVideo(mask_url, video_url, this.abortController?.signal);
	// 	this.merged_video_url = mergedVideoUrl;
	// }
}

export default VideoGenerationStore;
